import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { login, setAuthToken } from '../services/api';
import { useQueryClient } from '@tanstack/react-query';

export default function LoginPage({ onLogin }: { onLogin: (username: string) => void }) {
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const queryClient = useQueryClient();

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      {/* Gradient background matching top bar (semi-transparent) */}
      <Box sx={{ position: 'fixed', inset: 0, zIndex: -1, background: 'linear-gradient(135deg, rgba(25,118,210,0.4) 0%, rgba(255,138,101,0.4) 100%)' }} />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 64px)' }}>
        <Paper sx={{ width: 480, p: 4, borderRadius: 2, backdropFilter: 'blur(6px)', backgroundColor: 'rgba(255,255,255,0.7)' }} elevation={3}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box sx={{ width: 48, height: 48, borderRadius: 1, backgroundColor: '#1976d2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <LockOutlinedIcon sx={{ color: '#fff' }} />
          </Box>
          <Typography variant="h5">Sign in</Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Enter your username and password to continue.</Typography>

        <Box component="form" onSubmit={async (e) => {
          e.preventDefault();
          setLoading(true);
          try {
            const res = await login(username, password);
            if (res && res.status === 200) {
              const token = res.data?.token || res.data?.accessToken || res.data?.access_token || null;
              if (token) setAuthToken(token);
              // Clear react-query cache so any previous user's data isn't shown.
              try { queryClient.clear(); } catch (err) { /* ignore */ }
              onLogin(username);
            } else {
              const msg = res?.data?.message || `Login failed (status ${res?.status})`;
              window.alert(msg);
            }
          } catch (err: any) {
            window.alert(err?.message || 'Login request failed');
          } finally {
            setLoading(false);
          }
        }}>
          <TextField label="Username" value={username} onChange={(e) => setUsername(e.target.value)} fullWidth sx={{ mb: 2 }} autoFocus />
          <TextField label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} fullWidth sx={{ mb: 2 }} />
          <Button type="submit" variant="contained" fullWidth disabled={loading || !username || !password}>{loading ? 'Signing in...' : 'Sign in'}</Button>
        </Box>
      </Paper>
      </Box>
    </Box>
  );
}
